import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient, ApiError } from '../client';

export type OnboardingStep =
  | 'registered'
  | 'email_verified'
  | 'sectors_selected'
  | 'plan_selected'
  | 'payment_complete'
  | 'profile_complete';

export type OnboardingRegistrationType = 'company' | 'individual';

/** Returned by POST /auth/register/ */
export interface OnboardingStateResponse {
  token: string;
  registration_type: OnboardingRegistrationType;
  current_step: OnboardingStep;
  enterprise_uuid: string | null;
  email_verified: boolean;
}

/** Returned by GET /auth/onboarding-status/ and onboarding step mutations */
export interface OnboardingStatus {
  token: string;
  registration_type: OnboardingRegistrationType;
  current_step: OnboardingStep;
  enterprise_uuid: string | null;
  company_slug: string | null;
  email: string | null;
  email_verified: boolean;
  /** Step-specific data accumulated during onboarding (sectors, plan, etc.) */
  data: Record<string, unknown>;
  completed_at: string | null;
}

export interface SubmitOnboardingSectorsPayload {
  sectors: string[];
}

function getOnboardingRequestConfig(token?: string | null) {
  if (!token) {
    return undefined;
  }

  return {
    params: {
      token,
    },
  };
}

/**
 * Fetches the current onboarding step.
 * Pass the opaque token returned by POST /auth/register/ if the user is not yet authenticated.
 */
export function useOnboardingStatus(token?: string | null) {
  return useQuery<OnboardingStatus, ApiError>({
    queryKey: ['onboarding-status', token ?? null],
    queryFn: async () => {
      const response = await apiClient.get<OnboardingStatus>(
        '/auth/onboarding-status/',
        token ? { params: { token } } : undefined
      );
      return response.data;
    },
    enabled: token !== undefined,
  });
}

/**
 * Saves the user's sector selections and advances onboarding to `sectors_selected`.
 * Calls POST /auth/onboarding/sectors/
 */
export function useSubmitOnboardingSectors(token?: string | null) {
  return useMutation<OnboardingStatus, ApiError, SubmitOnboardingSectorsPayload>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<OnboardingStatus>(
        '/auth/onboarding/sectors/',
        payload,
        getOnboardingRequestConfig(token)
      );
      return response.data;
    },
  });
}

/**
 * Skips an optional onboarding step.
 * Calls POST /auth/onboarding/skip/
 */
export function useSkipOnboardingStep(token?: string | null) {
  return useMutation<OnboardingStatus, ApiError, { step: 'plan_selected' | 'profile_complete' }>({
    mutationFn: async (payload) => {
      const response = await apiClient.post<OnboardingStatus>(
        '/auth/onboarding/skip/',
        payload,
        getOnboardingRequestConfig(token)
      );
      return response.data;
    },
  });
}
